import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Lock, Mail, LogIn, Eye, EyeOff, CheckCircle2, Loader2, Store, ExternalLink } from 'lucide-react';
import { toast } from 'react-hot-toast';

export default function IkasConnect() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  
  // URL params
  const shopName = searchParams.get('shop') || '';
  
  // Step management: 'login' | 'select-brand' | 'connecting' | 'success'
  const [step, setStep] = useState('login');
  
  // Login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  // Brands state
  const [brands, setBrands] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  
  // Connection state
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState('');

  // Check if user is already logged in
  useEffect(() => {
    if (user) {
      setStep('select-brand');
      fetchBrands();
    }
  }, [user]);

  const fetchBrands = async () => {
    setLoadingBrands(true);
    try {
      const response = await fetch('https://api.glowytics.com/api/brands', {
        credentials: 'include'
      });
      const data = await response.json();
      setBrands(data.brands || []);
    } catch (error) {
      console.error('Fetch brands error:', error);
      toast.error('Failed to fetch brands');
    } finally {
      setLoadingBrands(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsLoggingIn(true);
    
    try {
      const response = await fetch('https://api.glowytics.com/api/auth/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }
      
      login(data.user);
      setStep('select-brand');
      fetchBrands();
      
    } catch (err) {
      console.error('Login error:', err);
      setLoginError(err.message || 'An unexpected error occurred.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleBrandSelect = async (brand) => {
    setSelectedBrand(brand);
    setStep('connecting');
    setIsConnecting(true);
    setConnectionError('');
    
    try {
      // Connect pending ikas token to selected brand
      const response = await fetch('https://api.glowytics.com/api/auth/ikas/connect-pending-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brand.id,
        },
        body: JSON.stringify({
          storeName: shopName,
          brandId: brand.id,
        }),
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to connect Ikas store');
      }
      
      setStep('success');
      toast.success(`ikas store connected to ${brand.name}!`);
      
    } catch (error) {
      console.error('Connection error:', error);
      setConnectionError(error.message || 'Failed to connect. Please try again.');
      setStep('select-brand');
    } finally {
      setIsConnecting(false);
    }
  };

  const goToDashboard = () => {
    if (selectedBrand) {
      navigate(`/dashboard/${selectedBrand.id}`);
    } else {
      navigate('/');
    }
  };

  // Render Login Step
  const renderLoginStep = () => (
    <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md">
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-3 mb-4">
          <img src="/Calisma-Yuzeyi-20@5x-4.png" alt="Glowytics" className="h-8" />
          <span className="text-gray-300 text-2xl">×</span>
          <div className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-lg">
            <Store className="w-5 h-5 text-gray-600" />
            <span className="font-medium text-gray-700">ikas</span>
          </div>
        </div>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Connect Your ikas Store</h1>
        {shopName && (
          <p className="text-sm text-gray-500">
            Store: <span className="font-medium text-gray-700">{shopName}</span>
          </p>
        )}
      </div>
      
      {loginError && (
        <div className="mb-6 rounded-lg bg-red-50 p-3 border border-red-200">
          <p className="text-sm text-red-700">{loginError}</p>
        </div>
      )}
      
      <form onSubmit={handleLogin} className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
              placeholder="you@company.com"
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type={showPassword ? 'text' : 'password'}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-10 pr-12 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
              placeholder="Enter password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>
        
        <button
          type="submit"
          disabled={isLoggingIn}
          className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoggingIn ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Logging in...
            </>
          ) : (
            <>
              <LogIn className="w-5 h-5" />
              Log In & Connect
            </>
          )}
        </button>
      </form>
      
      <div className="mt-6 pt-6 border-t border-gray-100 text-center">
        <p className="text-sm text-gray-500">
          Don't have an account?{' '}
          <a 
            href={`/signup?source=ikas&shop=${encodeURIComponent(shopName)}`}
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            Create Account
          </a>
        </p>
      </div>
    </div>
  );

  // Render Brand Selection Step
  const renderBrandSelectionStep = () => (
    <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-lg">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Store className="w-8 h-8 text-indigo-600" />
        </div>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Select a Brand</h1>
        <p className="text-sm text-gray-500">
          Choose which brand to connect with your ikas store
          {shopName && <span className="font-medium"> ({shopName})</span>}
        </p>
      </div>
      
      {connectionError && (
        <div className="mb-6 rounded-lg bg-red-50 p-3 border border-red-200">
          <p className="text-sm text-red-700">{connectionError}</p>
        </div>
      )}
      
      {loadingBrands ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          <span className="ml-2 text-gray-600">Loading brands...</span>
        </div>
      ) : brands.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500 mb-4">You don't have any brands yet.</p>
          <a 
            href="/brands/new"
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition"
          >
            Create Your First Brand
          </a>
        </div>
      ) : (
        <div className="space-y-3">
          {brands.map((brand) => (
            <button
              key={brand.id}
              onClick={() => handleBrandSelect(brand)}
              className="w-full flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-indigo-300 hover:bg-indigo-50 transition text-left group"
            >
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-indigo-100 transition">
                <span className="text-xl font-bold text-gray-400 group-hover:text-indigo-600 transition">
                  {brand.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">{brand.name}</h3>
                <p className="text-sm text-gray-500">
                  Created {new Date(brand.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div className="text-indigo-600 opacity-0 group-hover:opacity-100 transition">
                <ExternalLink className="w-5 h-5" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // Render Connecting Step
  const renderConnectingStep = () => (
    <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md text-center">
      <Loader2 className="w-16 h-16 animate-spin text-indigo-600 mx-auto mb-4" />
      <h1 className="text-xl font-semibold text-gray-900 mb-2">Connecting...</h1>
      <p className="text-sm text-gray-500">
        Linking <span className="font-medium">{shopName}</span> to <span className="font-medium">{selectedBrand?.name}</span>
      </p>
    </div>
  );

  // Render Success Step
  const renderSuccessStep = () => (
    <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md text-center">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <CheckCircle2 className="w-10 h-10 text-green-600" />
      </div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-2">Connected Successfully!</h1>
      <p className="text-gray-500 mb-2">
        Your ikas store <span className="font-medium text-gray-700">{shopName}</span>
      </p>
      <p className="text-gray-500 mb-6">
        is now connected to <span className="font-medium text-gray-700">{selectedBrand?.name}</span>
      </p>
      <button
        onClick={goToDashboard}
        className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition"
      >
        Go to Dashboard
        <ExternalLink className="w-4 h-4" />
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30"></div>
      </div>
      
      <div className="relative z-10">
        {step === 'login' && renderLoginStep()}
        {step === 'select-brand' && renderBrandSelectionStep()}
        {step === 'connecting' && renderConnectingStep()}
        {step === 'success' && renderSuccessStep()}
      </div>
    </div>
  );
}

